import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import { useAutoPrompt } from '@skyscanner-internal/global-components/login-form/helpers';
import { isCookieBannerClosed } from '@web-platform/shared-banana-redux/src/state';
import {
  getCreativesStatus,
  graphicPromos,
  homepageHeroes,
  homepageVideos,
} from '@web-platform/shared-banana-redux/src/state/combinedResults';
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import { SPONSORED_SERVICE_PRIORITY } from '@web-platform/shared-universal-constants/src/adverts';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { LOGIN_MODAL_REFERRERS } from '@web-platform/shared-universal-features-global-login-form';
import { loginVisibilityChanged } from '@web-platform/shared-universal-redux/src/actions/user';
import { getUserIdentifiers } from '@web-platform/shared-universal-redux/src/state';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import {
  logError,
  logOperationalEvent,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { useDispatch, useSelector } from 'react-redux';

export const LoginAutoPrompt = () => {
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector(getUserIdentifiers);
  const cookieBannerClosed = useSelector(isCookieBannerClosed);
  const isTabletOrSmaller = useMediaQuery(BREAKPOINTS.TABLET);
  const enableLoginAutoPrompt = getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_LOGIN_FORM_AUTO_PROMPT,
  );

  const creativesStatus = useSelector(getCreativesStatus);
  const allGraphicPromos = useSelector(graphicPromos);
  const allHomepageHeroes = useSelector(homepageHeroes);
  const allHomepageVideos = useSelector(homepageVideos);

  const creativesSettled =
    creativesStatus === 'ready' || creativesStatus === 'error' || creativesStatus === 'timeout';

  const isPaidAdDisplayed =
    creativesSettled &&
    [
      ...Object.values(allGraphicPromos),
      ...Object.values(allHomepageHeroes),
      ...Object.values(allHomepageVideos),
    ].some(
      (ad) =>
        ad.analyticsProperties?.priority === SPONSORED_SERVICE_PRIORITY.PREMIUM ||
        ad.analyticsProperties?.priority === SPONSORED_SERVICE_PRIORITY.STANDARD,
    );

  // canPrompt requires:
  // !isLoggedIn - User is logged out
  // !isTabletOrSmaller - Desktop only (not tablets or mobile web)
  // cookieBannerClosed - Cookie banner dismissed (avoid stacking modals)
  // creativesSettled - Creatives settled (wait for ad data before deciding)
  // !isPaidAdDisplayed - No paid ad displayed (don't compete with paid placements)
  const canPrompt =
    !isLoggedIn &&
    !isTabletOrSmaller &&
    cookieBannerClosed &&
    creativesSettled &&
    !isPaidAdDisplayed;

  useAutoPrompt({
    isFeatureEnabled: !!enableLoginAutoPrompt,
    canPrompt,
    onShouldOpen: () => dispatch(loginVisibilityChanged(true, LOGIN_MODAL_REFERRERS.AUTO_PROMPT)),
    v18__logger: {
      logOperationalEvent,
      logError: (error) =>
        logError({ component: 'LoginAutoPrompt', message: error.message, error }),
      logWarn,
    },
  });

  return null;
};
